/**
 * Hooks de TanStack Query para PO (Purchase Order).
 *
 * IMPORTANTE: cualquier mutación de PO puede disparar una auto-transición de
 * la PR origen (ADJ_PARCIAL/ADJ_TOTAL/EE_ABAS), por lo que TODAS las
 * mutaciones invalidan también las queries de PRs.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { buildRepos } from "@/core/data/repos";
import { registrarAuditoria } from "@/core/audit/registrarAuditoria";
import { deberiaEstarVencida } from "./poRules";
import { notificarTransicionPo } from "@core/mail/cableado";
import type { Po } from "@app-types/entities";

function invalidateAfterPoMutation(qc: ReturnType<typeof useQueryClient>, tenantId: string, poId?: string) {
  if (poId) void qc.invalidateQueries({ queryKey: ["po", tenantId, poId] });
  void qc.invalidateQueries({ queryKey: ["pos", tenantId] });
  // PR puede cambiar de estado por auto-transición
  void qc.invalidateQueries({ queryKey: ["prs", tenantId] });
  void qc.invalidateQueries({ queryKey: ["pr", tenantId] });
  void qc.invalidateQueries({ queryKey: ["prs-by-afe", tenantId] });
}

export function usePos(tenantId: string) {
  return useQuery({
    queryKey: ["pos", tenantId],
    queryFn: () => buildRepos(tenantId).po.list(),
    staleTime: 30_000,
  });
}

export function usePo(tenantId: string, id: string | null) {
  return useQuery({
    queryKey: ["po", tenantId, id],
    queryFn: () => (id ? buildRepos(tenantId).po.get(id) : Promise.resolve(null)),
    enabled: !!id,
  });
}

export function useCreatePo(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<Po, "id" | "createdAt" | "updatedAt" | "version">) =>
      buildRepos(tenantId).po.create(data),
    onSuccess: () => invalidateAfterPoMutation(qc, tenantId),
  });
}

export function useUpdatePo(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, version }: { id: string; data: Partial<Po>; version: number }) =>
      buildRepos(tenantId).po.update(id, data, version),
    onSuccess: (_, { id }) => invalidateAfterPoMutation(qc, tenantId, id),
  });
}

export function useDeletePo(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => buildRepos(tenantId).po.delete(id),
    onSuccess: (_, id) => invalidateAfterPoMutation(qc, tenantId, id),
  });
}

/**
 * Hook que escanea las POs cargadas y promueve a VENCIDA aquellas cuya fecha
 * de entrega calculada ya pasó sin fecha real registrada (estado EMITIDA o
 * EN_ESPERA_ENTREGA). Se ejecuta una sola vez por sesión por PO afectada.
 */
export function useAutoVencidas(tenantId: string, pos: Po[] | undefined, userEmail: string) {
  const qc = useQueryClient();
  const procesadas = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!pos || pos.length === 0) return;
    const candidatas = pos.filter(
      (p) => deberiaEstarVencida(p) && !procesadas.current.has(p.id),
    );
    if (candidatas.length === 0) return;
    const repo = buildRepos(tenantId).po;
    void Promise.all(
      candidatas.map(async (p) => {
        procesadas.current.add(p.id);
        try {
          const estadoAnterior = p.estado;
          await repo.changeState(
            p.id,
            "VENCIDA",
            userEmail || "system",
            "Auto-promoción: plazo cumplido sin entrega registrada",
          );
          void registrarAuditoria(tenantId, {
            usuario: userEmail || "system",
            accion: "PO_VENCIDA",
            entidad: "PO",
            entidadId: p.codigo ?? p.id,
            diff: {
              estadoAnterior,
              estadoNuevo: "VENCIDA",
              motivo: "Auto-promoción: plazo cumplido sin entrega registrada",
            },
          });
        } catch (err) {
          // Las transiciones inválidas (estado cambió en paralelo) son
          // esperables y no merecen alertar al usuario; sin embargo, dejamos
          // el rastro en consola para diagnóstico de regresiones.
          console.warn(
            "[useAutoVencidas] No se pudo auto-promover PO",
            p.id,
            err,
          );
        }
      }),
    ).then(() => {
      void qc.invalidateQueries({ queryKey: ["pos", tenantId] });
    });
  }, [pos, tenantId, userEmail, qc]);
}

export function useChangePoState(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      nextState,
      userEmail,
      userName,
      observaciones,
    }: {
      id: string;
      nextState: Po["estado"];
      userEmail: string;
      /** Nombre del usuario logueado para usar en el correo. Opcional. */
      userName?: string;
      observaciones?: string;
    }) => {
      const repo = buildRepos(tenantId).po;
      const antes = await repo.get(id);
      const estadoAnterior = antes?.estado;
      await repo.changeState(id, nextState, userEmail, observaciones);
      const despues = await repo.get(id);
      if (despues && estadoAnterior) {
        void registrarAuditoria(tenantId, {
          usuario: userEmail,
          accion: `PO_${nextState}`,
          entidad: "PO",
          entidadId: despues.codigo ?? id,
          diff: {
            estadoAnterior,
            estadoNuevo: nextState,
            ...(observaciones ? { motivo: observaciones } : {}),
          },
        });
        // Fire-and-forget: el correo no debe bloquear la UI ni revertir el save.
        void notificarTransicionPo({
          tenantId,
          po: despues,
          estadoAnterior,
          estadoNuevo: nextState,
          actor: { email: userEmail, ...(userName !== undefined && { nombre: userName }) },
          ...(observaciones !== undefined && { motivo: observaciones }),
        });
      }
    },
    onSuccess: (_, { id }) => invalidateAfterPoMutation(qc, tenantId, id),
  });
}
